import React from "react";
import { alpha } from "@mui/material/styles";
import { Box, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { CodeBlock, Expect, Hint, Step, useSteps } from "../guide/GuideParts";
import { Flow, FlowActor, FlowStep, LinkCards, PageHeader, Section } from "../guide/DocParts";
import { servers } from "../onprem/servers";
import { STATE_COLORS } from "../archmap/ArchMap";

// Internal API, ERP data gateway and jobs: formerly atgolorsdataapi (192.168.66.28, PM2), now Docker on dev-003

const server = servers.find((s) => s.id === "s3")!;
const SSH = `ssh ${server.user}@${server.ip}`;
const PORTAL = "https://portal.azure.com/#@genesiscloud.onmicrosoft.com/resource/subscriptions/2ee57a8d-3069-49cb-8889-ad7e370a791c/resourceGroups";

const containers = server.services.filter((s) => s.kind !== "job" && s.kind !== "monitor");

interface Schedule {
  id: string;
  name: string;
  days: number[]; // 0 = Monday
  from: number;
  to: number;
  every: string;
  enabled: boolean;
  text: string;
}

// From the Scheduler section of the Internal API appsettings
const JOBS: Schedule[] = [
  { id: "saphr", name: "SapHrSync", days: [0, 1, 2, 3, 4, 5], from: 5, to: 21, every: "every 60 min", enabled: false, text: "Reads bookings and personal data from SAP HR (OData)" },
  { id: "co2", name: "Co2Automatic", days: [0, 1, 2, 3, 4, 5, 6], from: 0, to: 1, every: "once a day (1440 min)", enabled: false, text: "CO2 calculation for companies 140, 310, 315 from Recy" },
  { id: "janitza", name: "JanitzaCollection", days: [0, 1, 2, 3, 4, 5, 6], from: 5, to: 6, every: "daily 05:00", enabled: false, text: "Equipment data points from Janitza, up to 4 in parallel" },
  { id: "rentri", name: "RentriValidation", days: [0, 1, 2, 3, 4], from: 16, to: 18, every: "every 30 min", enabled: false, text: "Validates Rentri orders in batches of 20" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const JobCalendar: React.FunctionComponent = () => {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  const today = (now.getDay() + 6) % 7;
  const hour = now.getHours() + now.getMinutes() / 60;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "170px repeat(7, minmax(80px, 1fr))", gap: "4px", minWidth: 760, fontSize: 12.5 }}>
        <div />
        {DAYS.map((d, i) => (
          <Box key={d} sx={{ textAlign: "center", fontWeight: i === today ? 700 : 500, color: i === today ? "primary.main" : "text.secondary" }}>
            {d}
            {i === today && " · today"}
          </Box>
        ))}
        {JOBS.map((job) => (
          <React.Fragment key={job.id}>
            <Tooltip title={job.text}>
              <Box sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: job.enabled ? STATE_COLORS.up : STATE_COLORS.disabled }} />
                {job.name}
              </Box>
            </Tooltip>
            {DAYS.map((d, i) => (
              <Tooltip key={d} title={job.days.includes(i) ? `${job.name}: ${String(job.from).padStart(2, "0")}:00–${String(job.to).padStart(2, "0")}:00, ${job.every}${job.enabled ? "" : " (disabled)"}` : "not running"}>
                <Box sx={{ position: "relative", height: 26, borderRadius: 1, background: (t) => alpha(t.palette.text.primary, 0.04) }}>
                  {job.days.includes(i) && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        bottom: 4,
                        left: `${(job.from / 24) * 100}%`,
                        width: `${Math.max(((job.to - job.from) / 24) * 100, 4)}%`,
                        borderRadius: 1,
                        background: job.enabled ? "#6d4c41" : `repeating-linear-gradient(45deg, #90a4ae, #90a4ae 4px, #cfd8dc 4px, #cfd8dc 8px)`,
                      }}
                    />
                  )}
                  {i === today && <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${(hour / 24) * 100}%`, width: 2, background: "#e53935" }} />}
                </Box>
              </Tooltip>
            ))}
          </React.Fragment>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Each cell is one day (00–24 h). Hatched = configured but <b>Enabled: false</b> in the Internal API appsettings. Red line = now.
      </Typography>
    </Box>
  );
};

type Action = "status" | "logs" | "restart" | "stats" | "shell";

const ACTIONS: Record<Action, { label: string; cmd: (c: string) => string; expect: string; text: string }> = {
  status: { label: "Is it running?", cmd: (c) => `docker ps -a --filter name=${c}`, expect: "STATUS “Up …”. “Restarting” or “Exited” means it crashed – look at the logs.", text: "Shows the container and its state." },
  logs: { label: "Show the logs", cmd: (c) => `docker logs --tail 200 -f ${c}`, expect: "the last 200 lines, then new lines live. Ctrl+C to stop watching (the container keeps running).", text: "Errors usually explain why a call or a job fails." },
  restart: { label: "Restart", cmd: (c) => `docker restart ${c}\ndocker ps --filter name=${c}`, expect: "the name again, then STATUS “Up a few seconds”.", text: "Use after a configuration change or when the service hangs. Running calls are interrupted for a few seconds." },
  stats: { label: "CPU / memory", cmd: (c) => `docker stats --no-stream ${c}`, expect: "CPU % and MEM USAGE of the container.", text: "Shows the current load." },
  shell: { label: "Open a shell", cmd: (c) => `docker exec -it ${c} sh`, expect: "a prompt inside the container. exit to leave.", text: "For experts only: look at files inside the container." },
};

const Operate: React.FunctionComponent = () => {
  const [container, setContainer] = React.useState(containers[0].name);
  const [action, setAction] = React.useState<Action>("status");
  const { isDone, toggle } = useSteps(`${container}|${action}`);
  const svc = containers.find((c) => c.name === container)!;
  return (
    <>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
        <TextField select size="small" label="Service" value={container} onChange={(e) => setContainer(e.target.value)} sx={{ minWidth: 280 }}>
          {containers.map((c) => (
            <MenuItem key={c.id} value={c.name}>
              {c.name} · {c.port}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={action} onChange={(_, v) => v && setAction(v)}>
          {(Object.keys(ACTIONS) as Action[]).map((a) => (
            <ToggleButton key={a} value={a}>
              {ACTIONS[a].label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {svc.note && <Hint>{svc.note}</Hint>}
      <Step n={1} title={`Connect to ${server.dns}`} where="pc" done={isDone(1)} onToggle={toggle(1)}>
        <CodeBlock code={SSH} />
      </Step>
      <Step n={2} title={ACTIONS[action].label} where="ssh" done={isDone(2)} onToggle={toggle(2)}>
        <p>{ACTIONS[action].text}</p>
        <CodeBlock code={ACTIONS[action].cmd(container)} />
        <Expect>{ACTIONS[action].expect}</Expect>
      </Step>
      <Hint type="warn">
        The compose files are in <code>/home/dev/docker</code>; data under <code>/home/docker-data</code>. Change configuration there and recreate with{" "}
        <code>docker compose up -d &lt;service&gt;</code> – a plain restart does not pick up compose changes. Portainer (https://10.192.193.6:9443) shows the same without a console.
      </Hint>
    </>
  );
};

const ERP_ACTORS: FlowActor[] = [
  { id: "client", name: "Kundenportal / Abfuhrkalender", color: "#0277bd" },
  { id: "rpx", name: "Proxy + balancer", sub: "dev-rpx", color: "#005f98" },
  { id: "gw", name: "ERP gateway API", sub: "GraphQL · Node.js", color: "#00897b" },
  { id: "redis", name: "Redis cache", color: "#c2185b" },
  { id: "erp", name: "ERP", sub: "RECY, RONA, Telltex, Kendox", color: "#7b4fc9" },
];

const ERP_FLOW: FlowStep[] = [
  { from: "client", to: "rpx", text: "A GraphQL query with a Bearer token arrives at the proxy." },
  { from: "rpx", to: "gw", text: "The balancer sends it to one of the redundant gateway APIs (RECY, RONA or Telltex)." },
  { from: "gw", to: "redis", text: "The API first looks in Redis whether the answer is already cached." },
  { from: "redis", to: "gw", text: "Cached → the answer comes from Redis without asking the ERP." },
  { from: "gw", to: "erp", text: "Not cached → the API asks the ERP and stores the answer in Redis." },
  { from: "gw", to: "client", text: "The answer goes back. PROD and DEV use RECY PROD, TEST and RELEASE use RECY TEST." },
];

const SMS_ACTORS: FlowActor[] = [
  { id: "recy", name: "Recy", sub: "orders", color: "#7b4fc9" },
  { id: "worker", name: "smsdailyorders", sub: "worker", color: "#6d4c41" },
  { id: "grid", name: "Event Grid", sub: "toursupdate", color: "#c2185b" },
  { id: "queue", name: "Queue", sub: "smsjobs", color: "#c2185b" },
  { id: "fn", name: "SMSDataFunctions", sub: "Azure Function", color: "#0078d4" },
  { id: "phone", name: "Customer", sub: "SMS via Vonage", color: "#43a047" },
];

const SMS_FLOW: FlowStep[] = [
  { from: "worker", to: "recy", text: "Every 5 seconds the worker collects orders with an allowed fleet state (49, 50, 51, 52, 53, 67, 246, 247)." },
  { from: "worker", to: "grid", text: "It writes one event per order into the Event Grid topic toursupdate." },
  { from: "grid", to: "queue", text: "The queue smsjobs subscribes to the topic …" },
  { from: "queue", to: "fn", text: "… and triggers SMSDataFunctions." },
  { from: "fn", to: "fn", text: "The function checks table aaSmsNotifications: was an SMS already sent today to this number for this order?" },
  { from: "fn", to: "phone", text: "If not, it sends the SMS with the Loacker number and logs it in aaSmsNotifications." },
];

const VEHICLES: [string, number][] = [
  ["Absetzer", 0],
  ["Kranwagen", 0],
  ["Abroller", 0],
  ["WC-LKW", 1],
  ["Frontlader", 2],
  ["Presswagen", 3],
];

const GRAPHQL = `query getGralInfo {
  generalInfo(CountryCode: "AT") {
    countryInfo { email }
    holidays { date }
    priceZones { containerTypesPrice { value containerTypeId } }
    containerShopSteps { id value }
    weightVolumePrice
  }
}`;

const GraphqlTest: React.FunctionComponent = () => {
  const { isDone, toggle } = useSteps("graphql");
  return (
    <>
      <Step n={1} title="Get a Bearer token" where="browser" done={isDone(1)} onToggle={toggle(1)}>
        <p>
          Sign in to <a href="https://mdm.genesiscloud.cc/portal" target="_blank" rel="noreferrer">MDM</a> and copy your token.
        </p>
      </Step>
      <Step n={2} title="Open the GraphQL endpoint" where="browser" done={isDone(2)} onToggle={toggle(2)}>
        <p>
          The containers on {server.dns}: <code>erpdatagateway-prod</code> (port 4445) and <code>erpdatagateway-re</code> (port 4446), path <code>/graphql</code>. Add the HTTP header:
        </p>
        <CodeBlock code={`{"Authorization": "Bearer <token>"}`} />
      </Step>
      <Step n={3} title="Run a query" where="browser" done={isDone(3)} onToggle={toggle(3)}>
        <CodeBlock label="GraphQL" code={GRAPHQL} />
        <Expect>
          JSON with <code>data.generalInfo</code>. An <code>errors</code> array with “jwt” means the token is missing or expired.
        </Expect>
      </Step>
      <Hint type="danger">
        The Redis cache can only be changed from its console. <code>FLUSHALL</code> deletes <b>all</b> cached answers – the next calls are slow until the cache is filled again.
      </Hint>
    </>
  );
};

export const DataApiJobsPage: React.FunctionComponent = () => {
  const [tab, setTab] = React.useState<"erp" | "sms" | "janitza">("erp");
  return (
    <Paper sx={{ p: 2, width: "100%" }}>
      <PageHeader
        title="Internal API & Jobs"
        subtitle={`${server.dns} · ${server.ip} · ${server.os} · Docker`}
        stats={[
          { label: "Containers", value: containers.length },
          { label: "Scheduled jobs", value: JOBS.length },
          { label: "Jobs enabled", value: JOBS.filter((j) => j.enabled).length },
        ]}
      />
      <Hint type="warn">
        The old server <b>atgolorsdataapi</b> (192.168.66.28, Windows with PM2 under C:\Program Files\iisnode\www) is retired. Its services now run as Docker containers on{" "}
        <b>{server.dns}</b> and are restarted with Docker or Portainer instead of PM2.
      </Hint>

      <Section title="Services" text="Click a service below to operate it">
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 1.5 }}>
          {containers.map((c) => (
            <Paper key={c.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderLeft: `4px solid ${c.kind === "proxy" ? "#005f98" : "#00897b"}` }}>
              <Typography variant="caption" color="text.secondary">
                {c.group}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontFamily: "Consolas, monospace", fontSize: 13.5 }}>{c.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Port {c.port}
                {c.containerPort ? ` → ${c.containerPort}` : ""}
              </Typography>
              {c.note && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  {c.note}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      </Section>

      <Section title="Operate a service" text="Pick the service and what you want to do">
        <Operate />
      </Section>

      <Section title="Job calendar" text="Scheduler jobs of the Internal API">
        <JobCalendar />
        <Hint>
          To switch a job on: set <code>Enabled: true</code> for it in the Scheduler section of the Internal API settings, then recreate <code>genesis-internal-api-pr</code>. Test it on{" "}
          <code>genesis-internal-api-dev</code> first.
        </Hint>
      </Section>

      <Section title="How it works">
        <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)} sx={{ mb: 1 }}>
          <ToggleButton value="erp">ERP data gateway</ToggleButton>
          <ToggleButton value="sms">Tours SMS</ToggleButton>
          <ToggleButton value="janitza">Janitza</ToggleButton>
        </ToggleButtonGroup>
        {tab === "erp" && (
          <>
            <Flow actors={ERP_ACTORS} steps={ERP_FLOW} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Test the ERP data gateway
            </Typography>
            <GraphqlTest />
          </>
        )}
        {tab === "sms" && (
          <>
            <Flow actors={SMS_ACTORS} steps={SMS_FLOW} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2, mt: 2 }}>
              <div>
                <Typography variant="subtitle2">Vehicle groups that get an SMS (sequence offset)</Typography>
                {VEHICLES.map(([v, n]) => (
                  <Box key={v} sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderBottom: 1, borderColor: "divider", py: 0.4 }}>
                    <span>{v}</span>
                    <b>+{n}</b>
                  </Box>
                ))}
              </div>
              <div>
                <Hint>SMS settings are in the Azure table SMSConfiguration (gscr1prstomasterdata1). The worker reads them at start: restart it after a change.</Hint>
                <Hint type="warn">Where smsdailyorders runs after the 66.28 shutdown still has to be documented.</Hint>
              </div>
            </Box>
            <Box sx={{ mt: 2 }}>
              <LinkCards
                links={[
                  { title: "Event Grid toursupdate", url: `${PORTAL}/gsc-r1-pr-rg-communicationservices-1/providers/Microsoft.EventGrid/topics/toursupdate/overview`, color: "#c2185b" },
                  { title: "Queue smsjobs", url: `${PORTAL}/gsc-r1-pr-rg-communicationservices-1/providers/Microsoft.Storage/storageAccounts/gscr1prstocommunication1/queueList`, color: "#c2185b" },
                  { title: "SMSDataFunctions", url: `${PORTAL}/gsc-r1-pr-rg-communicationservices-1/providers/Microsoft.Web/sites/SMSDataFunctions/appServices`, color: "#0078d4" },
                  { title: "SMS log (aaSmsNotifications)", url: `${PORTAL}/gsc-r1-pr-rg-communicationservices-1/providers/Microsoft.Storage/storageAccounts/gscr1prstocommunication1/tableList`, color: "#7b4fc9" },
                  { title: "Tours monitor", url: "https://gsc-r1-toursmonitor-at.azurewebsites.net/", color: "#43a047" },
                ]}
              />
            </Box>
          </>
        )}
        {tab === "janitza" && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>equipments-janitza-api-pr</b> (port 5000 on {server.dns}) collects every morning between 5 and 6 the data points of yesterday from all equipments configured in the Azure
              table and writes them to <code>dailyEquipmentDataPointValues</code>. The SCADA web app lets users add values such as operating hours or power by hand.
            </Typography>
            <Hint>New SCADA users: create the user in Azure AD B2C and add a role row to the table users in gscr1prstousers1 (Application AT_SCADA, RoleName e.g. DATACOLLECTION).</Hint>
            <LinkCards
              links={[
                { title: "SCADA web app", url: "https://scadaplant-at.genesiscloud.cc/portal/location-management", color: "#00897b" },
                { title: "SCADA App Service", url: `${PORTAL}/gsc-r1-pr-rg-linuxapps-2/providers/Microsoft.Web/sites/gsc-r1-pr-ase-scadaplantat-2/appServices`, color: "#0078d4" },
                { title: "Equipment tables", url: `${PORTAL}/gsc-r1-pr-rg-production-1/providers/Microsoft.Storage/storageAccounts/gscr1prstoprodat1/tableList`, color: "#7b4fc9" },
              ]}
            />
          </>
        )}
      </Section>

      <Section title="Code">
        <LinkCards
          links={[
            { title: "datagateway", text: "ERP data gateway (Node.js, GraphQL)", url: "https://dev.azure.com/GenesisCloudApps1/datagateway" },
            { title: "EquipmenDatapointAPI", text: "Janitza API (C#)", url: "https://dev.azure.com/GenesisCloudApps1/_git/GenesisCloudServices?path=/EquipmenDatapointAPI" },
            { title: "Communications / worker", text: "Tours SMS worker (Node.js)", url: "https://dev.azure.com/GenesisCloudApps1/_git/Communications?path=/worker" },
          ]}
        />
      </Section>
    </Paper>
  );
};
